import React, { useState, useEffect } from 'react';
import { useLayers } from '../../context/LayerContext';
import { useContainers } from '../../context/ContainerContext';
import { useGames } from '../../context/GameContext';
import { useHistory } from '../../context/HistoryContext';
import { useAuth } from '../../context/AuthContext';
import GameStateManager from '../../services/GameStateManager';
import './SaveGameModal.css';

const SaveGameModal = ({ isOpen, onClose, onSaveSuccess, gameName: initialGameName, existingGameId, tokens, selectedTokenIds, tokenGroups }) => {
  const [gameName, setGameName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');
  const [isOverwrite, setIsOverwrite] = useState(false);

  // קונטקסטים
  const { gameContext } = useGames(); // Assuming useGames provides general game settings if needed
  
  // Add full context objects
  const layerContext = useLayers();
  const containerContext = useContainers();
  const historyContext = useHistory();
  const authContext = useAuth(); // Already defined, but good for clarity

  // Create GameStateManager instance
  const gameStateManager = new GameStateManager();

  // איפוס הטופס כשהמודל נפתח
  useEffect(() => {
    if (isOpen) {
      setGameName(initialGameName || '');
      setDescription('');
      setIsPublic(false);
      setError('');
      setSaveProgress(0);
      setSaveStatus('');
      setIsSaving(false);
      // Only set isOverwrite if existingGameId is a valid numeric ID
      setIsOverwrite(existingGameId && !isNaN(parseInt(existingGameId)) && parseInt(existingGameId) > 0);
    }
  }, [isOpen, initialGameName, existingGameId]);

  // פונקציית שמירה
  const handleSave = async () => {
    if (!gameName.trim()) {
      setError('Game name is required');
      return;
    }

    if (!authContext || !authContext.currentUser || !authContext.currentUser.userId) {
      setError('Please login to save the game. User ID is missing.');
      setIsSaving(false);
      return;
    }

    setIsSaving(true);
    setError('');
    setSaveProgress(0);

    try {
      // Step 1: Collecting game data
      setSaveStatus('Collecting game data...');
      setSaveProgress(10);

      const contexts = {
        layerContext,
        containerContext,
        gameContext,
        historyContext,
        authContext,
        tokenContext: {
          tokens: tokens || [],
          setTokens: () => {}, // Dummy function since we're only collecting, not setting
          selectedTokenIds: selectedTokenIds || [],
          setSelectedTokenIds: () => {}, // Dummy function since we're only collecting, not setting
          tokenGroups: tokenGroups || [],
          setTokenGroups: () => {} // Dummy function since we're only collecting, not setting
        }
      };

      const gameState = await gameStateManager.collectGameState(contexts);
      setSaveProgress(30);

      // Step 2: Saving game
      setSaveStatus(isOverwrite ? 'Updating game...' : 'Saving game...');
      
      const saveOptions = {
        name: gameName.trim(),
        description: description.trim(),
        isPublic,
        userId: authContext.currentUser.userId,
        gameStateId: isOverwrite ? existingGameId : undefined
      };

      const saveResult = isOverwrite 
        ? await gameStateManager.updateGameState(existingGameId, gameState, saveOptions)
        : await gameStateManager.saveGameState(gameState, saveOptions);
      setSaveProgress(80);

      if (!saveResult.success) {
        throw new Error(saveResult.error);
      }

      // Step 3: Completion
      setSaveStatus('Save completed successfully!');
      setSaveProgress(100);

      // הודעה למשתמש
      setTimeout(() => {
        if (onSaveSuccess) {
          onSaveSuccess({
            gameStateId: saveResult.gameStateId,
            gameId: saveResult.gameStateId,
            name: gameName,
            size: saveResult.size
          });
        }
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error saving game:', error);
      setError(`Error saving game: ${error.message}`);
      setSaveStatus('');
      setSaveProgress(0);
    } finally {
      setIsSaving(false);
    }
  };

  // פונקציית ביטול
  const handleCancel = () => {
    if (!isSaving) {
      onClose();
    }
  };

  // הערכת גודל המשחק
  const estimateGameSize = () => {
    try {
      const layerCount = layerContext?.layers?.length || 0;
      const containerCount = containerContext?.containers?.length || 0;
      
      // הערכה גסה בהתבסס על מספר האלמנטים
      const estimatedKB = (layerCount * 50) + (containerCount * 30) + 10;
      
      if (estimatedKB < 1024) {
        return `~${estimatedKB} KB`;
      } else {
        return `~${(estimatedKB / 1024).toFixed(1)} MB`;
      }
    } catch (error) {
      return 'Unknown';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="save-game-modal-overlay">
      <div className="save-game-modal">
        <div className="modal-header">
          <h2>💾 {isOverwrite ? 'Update Game' : 'Save Game'}</h2>
          {!isSaving && (
            <button className="close-btn" onClick={handleCancel}>×</button>
          )}
        </div>

        <div className="modal-content">
          {!isSaving ? (
            <>
              {/* הודעה על עדכון */}
              {isOverwrite && (
                <div className="overwrite-notice">
                  <span className="notice-icon">🔄</span>
                  <p>You are updating an existing game. This will overwrite the current saved version.</p>
                </div>
              )}
              
              {/* טופס שמירה */}
              <div className="form-group">
                <label htmlFor="gameName">Game Name *</label>
                <input
                  id="gameName"
                  type="text"
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  placeholder="Enter game name"
                  maxLength={100}
                  disabled={isSaving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description (Optional)</label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the game"
                  maxLength={500}
                  rows={3}
                  disabled={isSaving}
                />
              </div>

              <div className="form-group toggle-group">
                <label htmlFor="privacy-toggle" style={{ color: 'white', marginBottom: '8px' }}>
                  Game Privacy
                </label>
                <button
                  id="privacy-toggle"
                  type="button"
                  className={`toggle-btn ${isPublic ? 'active' : ''}`}
                  onClick={() => setIsPublic(!isPublic)}
                  disabled={isSaving}
                  title={isPublic ? "Game is set to Public" : "Game is set to Private"}
                >
                  <i className={`fas ${isPublic ? 'fa-globe' : 'fa-user'}`}></i>
                  {isPublic ? 'Public' : 'Private'}
                </button>
                <small className="help-text">
                  {isPublic 
                    ? 'This game will be available to all users in the public library'
                    : 'This game will only be visible to you'
                  }
                </small>
              </div>

              {/* מידע על המשחק */}
              <div className="game-info">
                <h3>Game Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Layers:</span>
                    <span className="value">{layerContext?.layers?.length || 0}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Containers:</span>
                    <span className="value">{containerContext?.containers?.length || 0}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Estimated Size:</span>
                    <span className="value">{estimateGameSize()}</span>
                  </div>
                </div>
              </div>

              {/* הודעת שגיאה */}
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
            </>
          ) : (
            /* מסך התקדמות */
            <div className="save-progress">
              <div className="progress-header">
                <h3>Saving Game...</h3>
                <p>{saveStatus}</p>
              </div>
              
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${saveProgress}%` }}
                ></div>
              </div>
              
              <div className="progress-text">
                {saveProgress}%
              </div>

              {saveProgress === 100 && (
                <div className="success-message">
                  <span className="success-icon">✅</span>
                  Game saved successfully!
                </div>
              )}
            </div>
          )}
        </div>

        {/* כפתורי פעולה */}
        {!isSaving && (
          <div className="modal-actions">
            <button 
              className="btn btn-secondary" 
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={isSaving || !gameName.trim()}
            >
              💾 Save Game
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveGameModal; 